/**
 * Cache Manager
 * Orchestrates all caching strategies (memory, localStorage, IndexedDB)
 * Implements stale-while-revalidate and cache-first strategies
 */

import { getDB } from './db';
import { getMemoryCache } from './memory-cache';
import { getCacheConfig, getCacheKey, shouldCache } from './cache-config';
import { hasPerformanceCacheConsent } from './cookie-consent';

const PERSISTENT_CACHE_DISABLED_KEY = 'prepxai_persistent_cache_disabled';
const DEVICE_STORAGE_KEYS = new Set([
  'theme',
  'prepx_cookie_preferences',
  'prepx_cookie_consent',
  'prepxai_cookie_consent',
  PERSISTENT_CACHE_DISABLED_KEY,
]);
const SESSION_STORAGE_KEYS_TO_PRESERVE = new Set(['prepxai_system_status']);

class CacheManager {
  constructor() {
    // IndexedDB is opened lazily only after Performance consent is granted,
    // or when an explicit purge needs to remove previously cached data.
    this.db = null;
    this.memory = getMemoryCache();
    this.pendingRequests = new Map(); // Track in-flight requests
    this.updateSubscribers = new Map(); // Notify listeners of cache updates
    this.backgroundUpdateQueue = new Map(); // Track queued background update timers
    this.generation = 0; // Prevent requests from an old login session writing after logout
  }

  getPersistentStore() {
    if (!this.db) {
      this.db = getDB();
    }
    return this.db;
  }

  /**
   * Main cache getter - implements smart cache strategy
   */
  async get(endpoint, params = {}, options = {}) {
    if (!hasPerformanceCacheConsent()) {
      return { data: null, source: 'disabled' };
    }

    const key = getCacheKey(endpoint, params);
    const config = getCacheConfig(endpoint);
    const cacheGeneration = this.generation;

    // For real-time data, skip cache checks
    if (config.strategy === 'network-first') {
      return { data: null, source: 'network-required' };
    }

    // Check memory cache first (fastest)
    const memoryData = this.memory.get(key);
    if (memoryData) {
      console.log(`[Cache] HIT (memory): ${key}`);
      return { data: memoryData, source: 'memory' };
    }

    // Check IndexedDB for persistent data
    if (
      config.storage === 'indexeddb'
      && localStorage.getItem(PERSISTENT_CACHE_DISABLED_KEY) !== 'true'
    ) {
      const store = this.db || CacheManager.prototype.getPersistentStore.call(this);
      const dbData = await store.get(key);
      if (
        !hasPerformanceCacheConsent()
        || cacheGeneration !== this.generation
      ) {
        return { data: null, source: 'disabled' };
      }
      if (dbData) {
        // Restore to memory for faster access
        this.memory.set(key, dbData.data, config.cacheDuration);
        console.log(`[Cache] HIT (indexeddb): ${key}`);
        return { data: dbData.data, source: 'indexeddb', cached: true };
      }
    }

    // Check localStorage for lightweight data
    if (config.storage === 'localstorage') {
      const stored = localStorage.getItem(key);
      if (stored) {
        const entry = JSON.parse(stored);
        if (entry.expiresAt > Date.now()) {
          // Restore to memory
          this.memory.set(key, entry.data, config.cacheDuration);
          console.log(`[Cache] HIT (localstorage): ${key}`);
          return { data: entry.data, source: 'localstorage', cached: true };
        } else {
          localStorage.removeItem(key);
        }
      }
    }

    console.log(`[Cache] MISS: ${key}`);
    return { data: null, source: 'miss' };
  }

  /**
   * Set cache entry
   */
  async set(endpoint, params, data, options = {}) {
    if (
      options.cacheGeneration !== undefined
      && options.cacheGeneration !== this.generation
    ) {
      return false;
    }

    if (!hasPerformanceCacheConsent()) {
      return false;
    }

    const key = getCacheKey(endpoint, params);
    const config = getCacheConfig(endpoint);
    const cacheGeneration = options.cacheGeneration ?? this.generation;

    if (!shouldCache(endpoint)) {
      return;
    }

    // Always update memory cache
    this.memory.set(key, data, config.cacheDuration);

    // Store in persistent storage
    if (
      config.storage === 'indexeddb'
      && localStorage.getItem(PERSISTENT_CACHE_DISABLED_KEY) !== 'true'
    ) {
      const store = this.db || CacheManager.prototype.getPersistentStore.call(this);
      await store.set(key, data, {
        ttl: config.cacheDuration,
        etag: options.etag,
        apiVersion: options.apiVersion || '1.0',
      });

      if (
        !hasPerformanceCacheConsent()
        || cacheGeneration !== this.generation
      ) {
        this.memory.delete(key);
        await store.delete(key);
        return false;
      }
    } else if (config.storage === 'localstorage') {
      localStorage.setItem(
        key,
        JSON.stringify({
          data,
          expiresAt: Date.now() + config.cacheDuration,
          timestamp: Date.now(),
        }),
      );
    }

    console.log(`[Cache] SET: ${key} (${config.strategy} in ${config.storage})`);

    // Notify subscribers of update
    this.notifySubscribers(key, data);
    return true;
  }

  /**
   * Check if cache is stale
   */
  isStale(endpoint, params = {}) {
    const key = getCacheKey(endpoint, params);
    const config = getCacheConfig(endpoint);

    // Get the cache entry to check age
    const entry = this.memory.cache?.get(key);
    if (!entry) return true;

    // Compare entry age to stale threshold
    const age = Date.now() - entry.timestamp;
    const staleThreshold = config.staleTime || config.cacheDuration;
    return age > staleThreshold;
  }

  /**
   * Register callback for cache updates
   */
  subscribe(endpoint, params, callback) {
    const key = getCacheKey(endpoint, params);
    if (!this.updateSubscribers.has(key)) {
      this.updateSubscribers.set(key, []);
    }
    this.updateSubscribers.get(key).push(callback);

    // Return unsubscribe function
    return () => {
      const subs = this.updateSubscribers.get(key);
      const index = subs.indexOf(callback);
      if (index > -1) {
        subs.splice(index, 1);
      }
    };
  }

  /**
   * Notify all subscribers of cache update
   */
  notifySubscribers(key, data) {
    if (this.updateSubscribers.has(key)) {
      this.updateSubscribers.get(key).forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error('Cache update callback error:', error);
        }
      });
    }
  }

  /**
   * Queue a background update for stale data
   */
  queueBackgroundUpdate(endpoint, params, fetchFn) {
    const key = getCacheKey(endpoint, params);

    // Prevent duplicate updates
    if (this.backgroundUpdateQueue.has(key)) {
      return;
    }

    const cacheGeneration = this.generation;

    // Execute after a small delay to batch updates
    const timer = setTimeout(() => {
      this.backgroundUpdateQueue.delete(key);
      if (cacheGeneration === this.generation) {
        this.performBackgroundUpdate(endpoint, params, fetchFn, cacheGeneration);
      }
    }, 100);
    this.backgroundUpdateQueue.set(key, timer);
  }

  /**
   * Perform background update for stale data
   * NOTE: reportSuccess/reportFailure are NOT called here because the axios
   * response interceptor in CachedAPIClient already handles that. Calling them
   * here caused a feedback loop where failures were immediately cancelled by
   * the fallback response being treated as a success.
   */
  async performBackgroundUpdate(endpoint, params, fetchFn, cacheGeneration = this.generation) {
    try {
      const freshData = await fetchFn();
      if (
        cacheGeneration === this.generation
        && freshData !== null
        && freshData !== undefined
      ) {
        await this.set(endpoint, params, freshData, { cacheGeneration });
      }
    } catch (error) {
      console.debug('Background cache update skipped:', error.message);
    }
  }

  /**
   * Clear cache for specific endpoint
   */
  async invalidate(endpoint, params = null) {
    if (!hasPerformanceCacheConsent()) return;

    if (params) {
      const key = getCacheKey(endpoint, params);
      this.memory.delete(key);
      const store = this.db || CacheManager.prototype.getPersistentStore.call(this);
      await store.delete(key);
      localStorage.removeItem(key);
    } else if (endpoint) {
      // This is a simplified version - in a real app you'd want to 
      // iterate and find keys that match this endpoint.
      // For now, we only support specific param-based invalidation 
      // or global clear.
      console.warn('Endpoint-wide invalidation without params is not fully implemented. Use clearAll() for global flush.');
    } else {
      await this.clearAll();
    }
  }

  /**
   * Clear performance caches without removing authentication or functional state.
   */
  async clearPerformanceCache() {
    this.generation += 1;

    this.backgroundUpdateQueue.forEach((timer) => clearTimeout(timer));
    this.backgroundUpdateQueue.clear();
    this.pendingRequests.clear();
    this.updateSubscribers.clear();

    this.memory.clear();

    try {
      const store = this.db || CacheManager.prototype.getPersistentStore.call(this);
      await store.clear();
      localStorage.removeItem(PERSISTENT_CACHE_DISABLED_KEY);
    } catch (error) {
      // Quarantine persistent cache across reloads if its contents cannot be purged.
      localStorage.setItem(PERSISTENT_CACHE_DISABLED_KEY, 'true');
      console.error('IndexedDB cache flush failed:', error);
    }

    const apiCacheKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('/') || key.startsWith('http'))) {
        apiCacheKeys.push(key);
      }
    }
    apiCacheKeys.forEach(key => localStorage.removeItem(key));

    console.log('Performance cache flush complete');
  }

  /**
   * Flush all caches - used during logout to prevent account data leaking.
   * Performs a deep clear of memory, IndexedDB, and account-related browser storage.
   */
  async clearAll() {
    await CacheManager.prototype.clearPerformanceCache.call(this);

    // Remove all account-scoped localStorage, preserving device preferences.
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !DEVICE_STORAGE_KEYS.has(key)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    // Session storage is tab-scoped and may contain user-specific UI state.
    const sessionKeysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && !SESSION_STORAGE_KEYS_TO_PRESERVE.has(key)) {
        sessionKeysToRemove.push(key);
      }
    }
    sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
    
    console.log('Global cache flush complete');
  }

  /**
   * Get cache size and stats
   */
  async getStats() {
    const memoryStats = this.memory.getStats();
    const dbStats = hasPerformanceCacheConsent()
      ? await (this.db || CacheManager.prototype.getPersistentStore.call(this)).getStats()
      : { totalEntries: 0, totalSize: 0, expiredEntries: 0, entries: [] };

    return {
      memory: memoryStats,
      indexeddb: dbStats,
      pendingRequests: this.pendingRequests.size,
      backgroundUpdates: this.backgroundUpdateQueue.size,
    };
  }

  /**
   * Clear expired cache entries
   */
  async cleanupExpired() {
    if (!hasPerformanceCacheConsent()) return;
    const store = this.db || CacheManager.prototype.getPersistentStore.call(this);
    await store.clearExpired();
    this.memory.clear(); // Clear all memory cache for safety
  }

  getGeneration() {
    return this.generation;
  }
}

// Singleton instance
let cacheManager = null;

export const getCacheManager = () => {
  if (!cacheManager) {
    cacheManager = new CacheManager();
  }
  return cacheManager;
};

export default CacheManager;
